import { FormEvent, useEffect, useRef, useState } from 'react';
import { MdArrowForward, MdCallEnd } from 'react-icons/md';
import { Navigate, useLocation } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { BotPlaybackBars } from '../components/BotPlaybackBars';
import { DeviceControl } from '../components/DeviceControl';
import { LiveInsightCard } from '../components/LiveInsightCard';
import { SessionCountdown } from '../components/SessionCountdown';
import { TranscriptAvatar } from '../components/TranscriptAvatar';
import { useSession } from '../context/SessionContext';
import {
  acquirePreviewStream,
  attachVideoTrack,
  clearSavedDeviceIds,
  formatMediaError,
  listMediaDevices,
  readSavedCameraId,
  readSavedMicId,
  releaseVideoTracks,
  replaceTrackDevice,
  saveCameraId,
  saveMicId,
  type DeviceChoice,
} from '../lib/mediaDevices';
import {
  customerAssistantCardTitle,
  customerAssistantDisplayName,
  customerAssistantLoadingStatus,
  customerSessionDurationNote,
} from '../lib/customerCopy';
import { pathWithKeptQuery } from '../lib/sessionUrl';

const MORPH_MS = 650;
const MIC_REQUIRED_MSG = "You can't join the call with your microphone off";
/** Voice-only sessions — transcript is read-only until text send is supported. */
const TEXT_CHAT_COMPOSER_ENABLED = false;

/**
 * Single page for haircheck + live call. Avoids remount flashes when joining.
 * `/haircheck-returning` and `/conversation` both render this; join only soft-updates the URL.
 */
export function CallPage() {
  const location = useLocation();
  const {
    profile,
    agentInfo,
    messages,
    micMuted,
    cameraOff,
    toast,
    showToast,
    audioBlocked,
    enableAudio,
    connecting,
    reconnecting,
    connected,
    callPhase,
    joinMorphing,
    completeJoinMorph,
    sessionLimitEndsAtMs,
    hasCompletedSession,
    previewRef,
    remoteVideoRef,
    messagesRef,
    insightCard,
    liveInsightCardsEnabled,
    connectCall,
    tryResume,
    onMic,
    onCamera,
    onHangup,
  } = useSession();

  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [cameras, setCameras] = useState<DeviceChoice[]>([]);
  const [mics, setMics] = useState<DeviceChoice[]>([]);
  const [cameraId, setCameraId] = useState<string | null>(readSavedCameraId());
  const [micId, setMicId] = useState<string | null>(readSavedMicId());
  const [mediaError, setMediaError] = useState('');
  const [mediaReady, setMediaReady] = useState(false);
  /** True once local camera should sit in the top-right PiP (after morph / live). */
  const [localIsPip, setLocalIsPip] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const localPipRef = useRef<HTMLDivElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const transferredRef = useRef(false);
  const morphGenRef = useRef(0);

  const e2eVisual = import.meta.env.VITE_E2E_VISUAL === '1';
  const e2eLive = e2eVisual && location.pathname.includes('conversation');
  const e2ePrep = e2eVisual && location.pathname.includes('haircheck');

  const phase: 'prep' | 'connecting' | 'morphing' | 'live' = e2eLive
    ? 'live'
    : e2ePrep
      ? 'prep'
      : callPhase;

  const inLiveUi = phase === 'live' || phase === 'morphing';
  const prepLike = phase === 'prep' || phase === 'connecting';
  const showRemoteChrome = phase === 'morphing' || phase === 'live';
  const inCall = phase === 'morphing' || phase === 'live';

  const setLocalPipNode = (node: HTMLDivElement | null) => {
    localPipRef.current = node;
    previewRef.current = node;
  };

  /** Follow new rows only while the user is already near the bottom (don't yank if they scrolled up). */
  const stickChatToBottomRef = useRef(true);
  const onMessagesScroll = () => {
    const el = messagesRef.current;
    if (!el) return;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickChatToBottomRef.current = gap <= 96;
  };

  useEffect(() => {
    const el = messagesRef.current;
    if (!el || !stickChatToBottomRef.current) return;
    // Instant jump — smooth scroll on every row feels like the list is fighting you.
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    tryResume();
  }, [tryResume]);

  /**
   * Join morph via Web Animations API (not CSS class transitions).
   * Same DOM node shrinks from full-bleed → top-right PiP over the agent.
   */
  useEffect(() => {
    if (phase !== 'morphing') return;
    const pip = localPipRef.current;
    if (!pip) {
      completeJoinMorph();
      return;
    }

    const gen = ++morphGenRef.current;
    let finished = false;

    const finish = () => {
      if (finished || morphGenRef.current !== gen) return;
      finished = true;
      setLocalIsPip(true);
      pip.getAnimations().forEach((a) => a.cancel());
      pip.style.cssText = '';
      completeJoinMorph();
    };

    setLocalIsPip(false);
    pip.style.cssText = '';
    pip.getAnimations().forEach((a) => a.cancel());

    const raf = requestAnimationFrame(() => {
      if (morphGenRef.current !== gen) return;
      const stage = pip.parentElement;
      if (!stage) {
        finish();
        return;
      }
      const stageBox = stage.getBoundingClientRect();
      const first = pip.getBoundingClientRect();
      const targetW = Math.min(stageBox.width * 0.226, 235);
      const targetH = targetW / 1.46;
      const targetTop = 24;
      const targetLeft = stageBox.width - 18 - targetW;

      const animation = pip.animate(
        [
          {
            top: `${first.top - stageBox.top}px`,
            left: `${first.left - stageBox.left}px`,
            width: `${first.width}px`,
            height: `${first.height}px`,
            right: 'auto',
            bottom: 'auto',
            borderRadius: '12px',
            boxShadow: '0 0 0 rgba(0,0,0,0)',
          },
          {
            top: `${targetTop}px`,
            left: `${targetLeft}px`,
            width: `${targetW}px`,
            height: `${targetH}px`,
            right: 'auto',
            bottom: 'auto',
            borderRadius: '13px',
            boxShadow: '0 7px 20px rgba(0, 0, 0, 0.24)',
          },
        ],
        {
          duration: MORPH_MS,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'forwards',
        },
      );

      void animation.finished.then(finish).catch(finish);
    });

    const fallback = window.setTimeout(finish, MORPH_MS + 200);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(fallback);
    };
  }, [phase, completeJoinMorph]);

  useEffect(() => {
    if (phase === 'live' || e2eLive) setLocalIsPip(true);
  }, [phase, e2eLive]);

  useEffect(() => {
    if (!connecting && !joinMorphing && !connected) {
      transferredRef.current = false;
      setLocalIsPip(false);
      morphGenRef.current += 1;
      const pip = localPipRef.current;
      if (pip) {
        pip.getAnimations().forEach((a) => a.cancel());
        pip.style.cssText = '';
      }
    }
  }, [connecting, joinMorphing, connected]);

  useEffect(() => {
    if (e2eLive || connecting || joinMorphing || connected) return;
    let cancelled = false;

    async function startPreview() {
      if (e2eVisual) {
        setCameras([
          { deviceId: 'cam-1', label: 'FaceTime HD Camera' },
          { deviceId: 'cam-2', label: 'iPhone Camera' },
          { deviceId: 'cam-3', label: 'OBS Virtual Camera' },
        ]);
        setMics([
          { deviceId: 'mic-1', label: 'MacBook Microphone' },
          { deviceId: 'mic-2', label: 'AirPods Microphone' },
          { deviceId: 'mic-3', label: 'External USB Microphone' },
        ]);
        setCameraId('cam-1');
        setMicId('mic-1');
        setMediaReady(true);
        setMediaError('');
        return;
      }
      if (streamRef.current) {
        if (videoRef.current && !videoRef.current.srcObject) {
          videoRef.current.srcObject = streamRef.current;
        }
        setMediaReady(true);
        return;
      }
      try {
        const stream = await acquirePreviewStream({
          cameraId,
          micId,
          video: true,
          audio: true,
        });
        if (cancelled || transferredRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        const listed = await listMediaDevices();
        if (!cancelled) {
          setCameras(listed.cameras);
          setMics(listed.mics);
          const activeCam = stream.getVideoTracks()[0]?.getSettings().deviceId || null;
          const activeMic = stream.getAudioTracks()[0]?.getSettings().deviceId || null;
          if (activeCam) {
            setCameraId(activeCam);
            saveCameraId(activeCam);
          }
          if (activeMic) {
            setMicId(activeMic);
            saveMicId(activeMic);
          }
          // Mic-only preview: keep Start enabled; camera can stay off.
          if (!activeCam) setCameraOn(false);
        }
        setMediaError('');
        setMediaReady(true);
      } catch (e) {
        clearSavedDeviceIds();
        setCameraId(null);
        setMicId(null);
        setMediaError(formatMediaError(e));
        setMediaReady(false);
        // Best-effort device list so the ▾ menus are not permanently empty.
        try {
          const listed = await listMediaDevices();
          if (!cancelled) {
            setCameras(listed.cameras);
            setMics(listed.mics);
          }
        } catch {
          /* ignore */
        }
      }
    }

    void startPreview();
    return () => {
      cancelled = true;
      if (!transferredRef.current) {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- acquire once on mount / reset
  }, [connecting, joinMorphing, connected, e2eLive, e2eVisual]);

  useEffect(() => {
    const stream = streamRef.current;
    if (!stream) return;
    for (const track of stream.getAudioTracks()) track.enabled = micOn;
    if (videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = stream;
    }
  }, [micOn, mediaReady]);

  // Haircheck only: camera off must stop the device (LED), not just disable frames.
  useEffect(() => {
    if (connected || inCall || !mediaReady || e2eVisual) return;
    const stream = streamRef.current;
    if (!stream) return;

    if (!cameraOn) {
      releaseVideoTracks(stream);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        if (!stream.getVideoTracks().some((t) => t.readyState === 'live')) {
          await attachVideoTrack(stream, cameraId);
        } else {
          for (const t of stream.getVideoTracks()) t.enabled = true;
        }
        if (!cancelled && videoRef.current) videoRef.current.srcObject = stream;
      } catch (e) {
        if (!cancelled) setMediaError(formatMediaError(e));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cameraOn, connected, inCall, mediaReady, cameraId, e2eVisual]);

  // Strip any non-React video LiveKit may have injected (duplicate local player).
  useEffect(() => {
    const pip = localPipRef.current;
    if (!pip) return;
    const strip = () => {
      const reactVideo = videoRef.current;
      for (const node of Array.from(pip.querySelectorAll('video'))) {
        if (node !== reactVideo) node.remove();
      }
    };
    strip();
    const mo = new MutationObserver(strip);
    mo.observe(pip, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    const el = remoteVideoRef.current;
    if (!el) return;
    const sync = () => {
      el.style.display = el.srcObject ? 'block' : 'none';
    };
    sync();
    const id = window.setInterval(sync, 500);
    return () => window.clearInterval(id);
  }, [remoteVideoRef, connected, phase]);

  if (hasCompletedSession) {
    return <Navigate to={pathWithKeptQuery('/complete')} replace />;
  }

  const firstName = profile?.firstName || 'User';
  const lastName = profile?.lastName || 'Unknown';
  const displayName = `${firstName} ${lastName}`.trim();
  const initials =
    `${(firstName[0] || '').toUpperCase()}${(lastName[0] || '').toUpperCase()}` || 'YO';
  const agentProfileLoading = !agentInfo;
  const agentName = customerAssistantDisplayName(agentInfo?.name);
  const agentRole = (agentInfo?.role || '').trim();
  const agentPoster = (agentInfo?.avatarUrl || '').trim() || null;
  const photoStyle = agentPoster
    ? {
        backgroundImage: `url(${agentPoster})`,
        backgroundSize: 'cover' as const,
        backgroundPosition: 'center' as const,
        color: 'transparent' as const,
      }
    : undefined;

  let readyText = 'Checking camera and microphone…';
  let readyWarn = false;
  let readyError = false;
  if (phase === 'live') {
    readyText = 'Good connection';
  } else if (phase === 'morphing') {
    readyText = 'Good connection';
  } else if (phase === 'connecting') {
    readyText = 'Connecting…';
  } else if (mediaError) {
    readyText = mediaError;
    readyError = true;
  } else if (!mediaReady) {
    readyText = 'Checking camera and microphone…';
  } else if (!micOn) {
    readyError = true;
    readyText = MIC_REQUIRED_MSG;
  } else if (!cameraOn) {
    readyWarn = true;
    readyText = 'Camera is off — you can still start with microphone only';
  } else {
    readyText = 'Good connection';
  }

  const startBlocked =
    connecting || joinMorphing || Boolean(mediaError) || !mediaReady || e2eLive;

  function onToggleMic() {
    setMicOn((prev) => {
      const next = !prev;
      if (!next) showToast(MIC_REQUIRED_MSG);
      return next;
    });
  }

  async function onSelectCamera(id: string) {
    try {
      if (!streamRef.current) {
        const stream = await acquirePreviewStream({
          cameraId: id,
          micId,
          video: true,
          audio: true,
        });
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        const listed = await listMediaDevices();
        setCameras(listed.cameras);
        setMics(listed.mics);
        setCameraOn(Boolean(stream.getVideoTracks().length));
        setMediaReady(true);
      } else {
        if (!streamRef.current.getVideoTracks().some((t) => t.readyState === 'live')) {
          await attachVideoTrack(streamRef.current, id);
          setCameraOn(true);
        } else {
          await replaceTrackDevice(streamRef.current, 'video', id);
        }
        if (videoRef.current) videoRef.current.srcObject = streamRef.current;
      }
      setCameraId(id);
      saveCameraId(id);
      setMediaError('');
    } catch (e) {
      setMediaError(formatMediaError(e));
    }
  }

  async function onSelectMic(id: string) {
    try {
      if (!streamRef.current) {
        const stream = await acquirePreviewStream({
          cameraId,
          micId: id,
          video: true,
          audio: true,
        });
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        const listed = await listMediaDevices();
        setCameras(listed.cameras);
        setMics(listed.mics);
        setCameraOn(Boolean(stream.getVideoTracks().length));
        setMediaReady(true);
      } else {
        await replaceTrackDevice(streamRef.current, 'audio', id);
      }
      setMicId(id);
      saveMicId(id);
      setMediaError('');
    } catch (e) {
      setMediaError(formatMediaError(e));
    }
  }

  function onStart(e: FormEvent) {
    e.preventDefault();
    if (!micOn) {
      showToast(MIC_REQUIRED_MSG);
      return;
    }
    if (startBlocked) return;
    transferredRef.current = true;
    void connectCall({
      firstName,
      lastName,
      cameraOn,
      micOn,
      mediaStream: streamRef.current,
      videoDeviceId: cameraId,
      audioDeviceId: micId,
    });
  }

  const localHidden = prepLike ? !cameraOn : cameraOff;
  const insightCardsOn =
    liveInsightCardsEnabled === true || agentInfo?.liveInsightCardsEnabled === true;
  const showInsightStack = insightCardsOn && localIsPip && inLiveUi;

  return (
    <div
      className={`conversation-page call-page${showInsightStack ? ' has-insight-stack' : ''}`}
      data-phase={phase}
    >
      <AppHeader
        greeting={`Hi, ${displayName}`}
        homeTo={pathWithKeptQuery('/haircheck-returning')}
      />

      <main className="conversation-shell">
        <div className="call-primary">
          <section
            className={`video-stage${inCall && sessionLimitEndsAtMs ? ' has-session-countdown' : ''}`}
            aria-label={
              showRemoteChrome
                ? `Live video conversation with ${agentName}`
                : 'Camera preview'
            }
          >
            <img
              className="agent-video video-reference"
              src={agentPoster || undefined}
              alt={`${agentName}${agentRole ? `, your ${agentRole}` : ''}`}
              fetchPriority="high"
              decoding="async"
              hidden={!agentPoster || !showRemoteChrome}
            />
            <video
              ref={remoteVideoRef}
              className="agent-video"
              autoPlay
              playsInline
              style={{ display: 'none', objectFit: 'cover' }}
            />

            <BotPlaybackBars active={showRemoteChrome && (phase === 'live' || phase === 'morphing')} />

            {sessionLimitEndsAtMs ? (
              <SessionCountdown
                endsAtMs={sessionLimitEndsAtMs}
                visible={inCall && showRemoteChrome}
              />
            ) : null}

            <div
              className={`local-pip${localIsPip ? ' is-pip' : ''}`}
              ref={setLocalPipNode}
              aria-label="Your camera preview"
            >
              {e2eVisual ? (
                <img
                  className="self-preview-video"
                  src="/assets/haircheck-camera.png"
                  alt="Your camera preview"
                  hidden={localHidden}
                />
              ) : (
                <video
                  ref={videoRef}
                  className="self-preview-video"
                  autoPlay
                  playsInline
                  muted
                  hidden={localHidden}
                  aria-hidden={localHidden}
                />
              )}
              <div className="camera-disabled" hidden={!localHidden} aria-hidden={!localHidden}>
                <span>{initials}</span>
              </div>
              <span className="preview-label" hidden={prepLike}>
                You
              </span>
            </div>

            {showInsightStack ? (
              <>
                <div className="agent-pip is-pip" aria-label={`${agentName} preview`}>
                  {agentPoster ? (
                    <img className="agent-pip-img" src={agentPoster} alt="" />
                  ) : (
                    <div className="agent-pip-fallback" aria-hidden>
                      <span>{(agentName[0] || '?').toUpperCase()}</span>
                    </div>
                  )}
                  <span className="preview-label">{agentName}</span>
                </div>
                <LiveInsightCard
                  key="live-insight"
                  incoming={insightCard}
                  visible={phase === 'live' || phase === 'morphing'}
                />
              </>
            ) : null}

            <div
              className={`connection-pill${readyError ? ' is-error' : readyWarn ? ' is-warn' : ''}`}
              aria-label={`Connection status: ${readyText}`}
              hidden={inCall}
            >
              <span className="status-dot" aria-hidden="true" />
              <span>{readyText}</span>
            </div>

            <div
              className="toast call-stage-toast"
              role="status"
              aria-live="polite"
              hidden={!toast || (audioBlocked && inCall)}
            >
              {toast}
            </div>

            <button
              type="button"
              className="toast call-stage-toast call-stage-audio-unlock"
              hidden={!audioBlocked || !inCall}
              onClick={() => void enableAudio()}
            >
              Tap to enable sound
            </button>

            <div
              className={`device-controls${inCall ? ' is-in-call' : ''}`}
              role="group"
              aria-label={inCall ? 'Call controls' : 'Camera and microphone controls'}
            >
              <DeviceControl
                kind="camera"
                enabled={inCall ? !cameraOff : cameraOn}
                devices={cameras}
                selectedId={cameraId}
                disabled={phase === 'connecting'}
                onToggle={() => {
                  if (inCall) void onCamera();
                  else setCameraOn((v) => !v);
                }}
                onSelect={(id) => void onSelectCamera(id)}
              />
              <DeviceControl
                kind="microphone"
                enabled={inCall ? !micMuted : micOn}
                devices={mics}
                selectedId={micId}
                disabled={phase === 'connecting'}
                onToggle={() => {
                  if (inCall) void onMic();
                  else onToggleMic();
                }}
                onSelect={(id) => void onSelectMic(id)}
              />
              {inCall ? (
                <button
                  className="device-toggle device-end-call"
                  type="button"
                  aria-label="End call"
                  onClick={onHangup}
                >
                  <MdCallEnd aria-hidden="true" />
                </button>
              ) : null}
            </div>
          </section>

          <form className="start-session-form" onSubmit={onStart} aria-hidden={!prepLike}>
            <button
              className="primary-action"
              type="submit"
              disabled={startBlocked || agentProfileLoading}
              aria-busy={connecting || agentProfileLoading}
            >
              <MdArrowForward aria-hidden="true" />
              <span>
                {connecting
                  ? reconnecting
                    ? 'Reconnecting…'
                    : 'Connecting…'
                  : agentProfileLoading
                    ? 'Loading assistant…'
                    : `Start session with ${agentName}`}
              </span>
            </button>
          </form>
        </div>

        <aside
          className="agent-card surface"
          aria-labelledby="agentHeading"
          aria-hidden={!prepLike}
        >
          <h1 id="agentHeading">{customerAssistantCardTitle()}</h1>
          {agentProfileLoading ? (
            <div className="agent-card-profile agent-card-profile--loading" aria-busy="true">
              <div className="agent-photo agent-photo--skeleton" aria-hidden="true" />
              <p className="agent-card-loading-status">{customerAssistantLoadingStatus()}</p>
              <div className="agent-card-skeleton-lines" aria-hidden="true">
                <span />
                <span />
              </div>
            </div>
          ) : (
            <div className="agent-card-profile">
              <div
                className={`agent-photo${agentPoster ? '' : ' is-empty'}`}
                role="img"
                aria-label={agentPoster ? `Portrait of ${agentName}` : undefined}
                style={photoStyle}
              >
                {!agentPoster ? (
                  <span aria-hidden="true">{(agentName[0] || '?').toUpperCase()}</span>
                ) : null}
              </div>
              <h2>{agentName}</h2>
              {agentRole ? <p className="agent-role">{agentRole}</p> : null}
            </div>
          )}
          <div className="haircheck-guidance">
            <p className="haircheck-guidance-title">For a clear, comfortable session</p>
            <ul className="haircheck-guidance-list">
              <li>
                Choose a quiet place and limit background noise so your digital assistant can hear you easily.
              </li>
              <li>
                Stable lighting helps if you use your camera — it keeps the experience smooth and personal.
              </li>
            </ul>
            <p className="haircheck-guidance-duration">
              {customerSessionDurationNote(agentInfo?.sessionMaxMinutes)}
            </p>
          </div>
        </aside>

        <aside
          className="conversation-panel surface"
          aria-labelledby="conversationTitle"
          aria-hidden={prepLike}
        >
          <div className="conversation-live">
            <div className="conversation-heading">
              <h1 id="conversationTitle">Conversation with {agentName}</h1>
              {agentRole ? <p>{agentRole}</p> : null}
            </div>

            <div
              className="messages"
              id="messages"
              role="log"
              aria-live="off"
              aria-relevant="additions"
              ref={messagesRef}
              onScroll={onMessagesScroll}
            >
              {messages.length === 0 && phase === 'morphing' && (
                <p className="messages-empty" style={{ padding: 16, color: '#7182b5' }}>
                  Starting…
                </p>
              )}
              {messages.map((m) => {
                const isDebug = m.kind === 'live_perception_debug' || m.author === 'Live Perception';
                const isAgent = m.author === 'Agent';
                const hrefCandidate = (m.url || m.text || '').trim();
                const href = /^https?:\/\//i.test(hrefCandidate)
                  ? hrefCandidate
                  : m.url && /^https?:\/\//i.test(m.url)
                    ? m.url.trim()
                    : '';
                const isLink =
                  Boolean(href) &&
                  (m.kind === 'link' || /^https?:\/\//i.test((m.text || '').trim()));
                return (
                  <article
                    key={m.key}
                    className={`message ${isDebug ? 'lp-debug-message' : isAgent ? 'agent-message' : 'user-message'}`}
                  >
                    <TranscriptAvatar
                      variant={isDebug ? 'live-perception' : isAgent ? 'agent' : 'user'}
                      agentName={agentName}
                      agentAvatarUrl={agentInfo?.avatarUrl}
                    />
                    <div className="message-body">
                      <div className="message-meta">
                        <strong>{isDebug ? 'Live Perception' : isAgent ? agentName : 'You'}</strong>
                        <span>·</span>
                        <time>{m.time}</time>
                      </div>
                      {isLink ? (
                        <p className="message-bubble">
                          <a href={href} target="_blank" rel="noopener noreferrer">
                            {m.text || href}
                          </a>
                        </p>
                      ) : (
                        <p className="message-bubble">{m.text}</p>
                      )}
                      {isDebug && m.frameUrl ? (
                        <img className="lp-debug-frame" src={m.frameUrl} alt="" />
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>

            {TEXT_CHAT_COMPOSER_ENABLED ? (
              <form className="composer" id="composer" onSubmit={(e) => e.preventDefault()}>
                <label className="sr-only" htmlFor="messageInput">
                  Type a message
                </label>
                <input
                  id="messageInput"
                  name="message"
                  type="text"
                  placeholder="Type a message…"
                  autoComplete="off"
                  disabled={!inLiveUi}
                />
              </form>
            ) : null}
          </div>
        </aside>
      </main>
    </div>
  );
}

/** @deprecated Use CallPage — kept for route imports. */
export const HaircheckReturningPage = CallPage;
/** @deprecated Use CallPage — kept for route imports. */
export const ConversationPage = CallPage;
